import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import useSWR from 'swr';
import { useRouter } from 'next/router';
import { fetcher } from '../lib/fetcher';
import { Plan } from '../models/plan';
import useUser from '../hooks/useUser';
import { useAppBridge } from '@shopify/app-bridge-react';
import { ModalContext } from './ModalContext';

interface ModalArgs {
  title?: string;
  content?: React.ReactNode;
}

interface PlanState {
  currentPlan: Plan | null;
  plans: Plan[] | null;
}

export interface PlanContextValue {
  show: (args?: ModalArgs) => void;
  plan: PlanState;
}

// Default is a cast, not null: every consumer lives under the provider, and the
// existing `useContext` destructures must keep compiling unchanged (B1).
export const PlanContext = createContext<PlanContextValue>({} as PlanContextValue);

export const PlanProvider = ({ children }: { children: React.ReactNode }) => {
  const app = useAppBridge();
  const router = useRouter();
  const { show: showModal, hide: hideModal } = useContext(ModalContext);
  const [plan, setPlan] = useState<PlanState>({ currentPlan: null, plans: null });
  const { data: plans } = useSWR<Plan[]>(`${process.env.APP_URL}/api/plans`, (input) =>
    fetcher(app, input),
  );
  const { user } = useUser();

  useEffect(() => {
    if (plans && plan.plans === null) {
      setPlan((prevState) => ({ ...prevState, plans }));
    }
    if (user && plan.currentPlan === null) {
      setPlan((prevState) => ({ ...prevState, currentPlan: user.plan }));
    }
  }, [user, plan, plans]);

  // Render the plan-limit upsell through the standard app Modal. Callers pass an
  // HTML string as `content` (e.g. "<b>Basic</b> plan"); wrap it so the markup
  // renders instead of showing raw tags.
  const show = useCallback(
    (args: ModalArgs = {}) => {
      const content =
        typeof args.content === 'string' ? (
          <div dangerouslySetInnerHTML={{ __html: args.content }} />
        ) : (
          args.content
        );
      showModal({
        title: args.title ?? "Oops! You've exceeded your plan limits.",
        content,
        primaryAction: {
          content: 'Upgrade plan',
          onAction: () => {
            hideModal();
            router.push('/plans');
          },
        },
        secondaryAction: {
          content: 'Cancel',
        },
      });
    },
    [showModal, hideModal, router],
  );

  return (
    <PlanContext.Provider
      value={{
        show,
        plan,
      }}
    >
      {children}
    </PlanContext.Provider>
  );
};
